import { useParams } from 'react-router-dom'
import { ProductDataForm } from 'components/Forms'
import StandardContent from 'components/StandardContent/StandardContent'
import VariantData from 'components/VariantData/VariantData'
import { createCsApiUrlFromEndpoint } from 'utils'
import useApi, { type ApiResponse } from 'hooks/useApi'
import type { FEApiError } from 'utils/errorHandling'
import type { productTabsIds } from 'components/Tabs/tabs-type'
import type { ProductsPartialUpdatePayload } from '@contracts'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils.d'
import type { Dispatch, SetStateAction } from 'react'
import type { ProductsDetailDataV5 } from 'utils/utils.d'

interface RenderNotGeneratedProductProps {
  activeTab: productTabsIds
  productAPI: ApiResponse<ProductsDetailDataV5 | null>
  setToastDetails: Dispatch<SetStateAction<ToastDetails | undefined>>
  isProductDataFormActive: boolean
  setIsProductDataFormActive: Dispatch<SetStateAction<boolean>>
  setShowIconInTab: Dispatch<SetStateAction<boolean[]>>
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>
}

export default function RenderNotGeneratedProduct({
  activeTab,
  productAPI,
  setToastDetails,
  isProductDataFormActive,
  setIsProductDataFormActive,
  setShowIconInTab,
  setHasUnsavedChanges,
}: RenderNotGeneratedProductProps) {
  const { product_id } = useParams()

  const { fetchData: getProduct, data: getProductData, isLoading: isGetProductLoading } = productAPI

  const {
    fetchData: patchProduct,
    url: patchProductUrl,
    isLoading: isPatchProductLoading,
  } = useApi<ProductsDetailDataV5>(createCsApiUrlFromEndpoint(`products/${product_id}`))

  const handleProductEdits = async (data: ProductsPartialUpdatePayload) => {
    try {
      await patchProduct(patchProductUrl, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      await getProduct()

      setToastDetails({
        message: 'Edits saved successfully',
        type: 'success',
      })
      setIsProductDataFormActive(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  return (
    <>
      {activeTab === 'product_data' ? (
        <ProductDataForm
          productData={getProductData!}
          isFormActive={isProductDataFormActive}
          areActionsVisible
          isApiLoading={isGetProductLoading || isPatchProductLoading}
          setHasUnsavedChanges={setHasUnsavedChanges}
          tabIconState={setShowIconInTab}
          onFormEdit={() => setIsProductDataFormActive(true)}
          onFormCancel={() => setIsProductDataFormActive(false)}
          onFormSubmit={handleProductEdits}
        />
      ) : activeTab === 'variant_data' ? (
        <VariantData productAPI={productAPI} setToastDetails={setToastDetails} />
      ) : (
        <StandardContent productData={getProductData!} />
      )}
    </>
  )
}
